<script setup lang="ts">
import { watch, onMounted, onUnmounted } from 'vue'
import { X } from 'lucide-vue-next'
import { useFocusTrap } from '@/composables/useFocusTrap'

const props = withDefaults(defineProps<{
  show: boolean
  title: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full'
  /** Prevent closing on backdrop click or Escape */
  persistent?: boolean
  /** Hide the default header (use #header slot for custom) */
  hideHeader?: boolean
}>(), {
  size: 'lg',
  persistent: false,
  hideHeader: false
})

const emit = defineEmits<{
  close: []
}>()

const { trapRef } = useFocusTrap()

// Mobile: all modals go near-full-width. Desktop: respect size prop.
const sizeClass: Record<string, string> = {
  sm: 'w-full sm:max-w-sm',
  md: 'w-full sm:max-w-md',
  lg: 'w-full sm:max-w-lg',
  xl: 'w-full sm:max-w-xl',
  '2xl': 'w-full sm:max-w-2xl',
  '3xl': 'w-full sm:max-w-3xl',
  '4xl': 'w-full sm:max-w-4xl',
  full: 'w-full max-w-[95vw]'
}

function requestClose() {
  if (!props.persistent) {
    emit('close')
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show) {
    requestClose()
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})

watch(() => props.show, (val) => {
  document.body.style.overflow = val ? 'hidden' : ''
  if (val) {
    trapRef.value && requestAnimationFrame(() => {
      const focusable = trapRef.value?.querySelector<HTMLElement>(
        'input:not([disabled]),select:not([disabled]),textarea:not([disabled]),button:not([disabled]),[tabindex]:not([tabindex="-1"])'
      )
      focusable?.focus()
    })
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="show"
        class="modal-overlay backdrop-blur-sm"
        @click.self="requestClose"
      >
        <div
          ref="trapRef"
          class="modal-content"
          :class="sizeClass[size] || 'max-w-lg'"
          @click.stop
        >
          <!-- Header -->
          <slot v-if="!hideHeader" name="header">
            <div class="modal-header">
              <h2 class="text-lg font-semibold text-surface-100">{{ title }}</h2>
              <button
                @click="emit('close')"
                class="p-1 text-surface-400 hover:text-surface-100 rounded transition-colors"
              >
                <X class="w-5 h-5" />
              </button>
            </div>
          </slot>

          <!-- Body -->
          <div class="modal-body">
            <slot />
          </div>

          <!-- Footer -->
          <slot name="footer" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.15s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
